import { create } from "zustand";
import { persist } from "zustand/middleware";
import { User } from "@/gql/graphql";
import { AuthEvent, authEventEmitter } from "@/lib/authorization/auth-events";
import { AuthError, type AuthTokens, authService } from "@/lib/services/auth.service";
import { useSalonStore } from "@/lib/store/salon";

// Re-export for convenience
export type { User };

/**
 * Authentication status enum for clear state management
 */
export enum AuthStatus {
  UNINITIALIZED = "uninitialized", // Initial state, auth not checked yet
  INITIALIZING = "initializing", // Currently validating tokens/session
  READY = "ready", // Initialization complete, auth state is known
}

/**
 * Clean authentication state interface focused solely on auth concerns
 */
interface AuthState {
  // Core authentication data
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;

  // Authentication status
  status: AuthStatus;
  isAuthenticated: boolean;
  isRefreshing: boolean;

  // Error handling
  authError: AuthError | null;

  // Actions
  login: (tokens: AuthTokens, user: User) => void;
  updateUser: (user: User) => void;
  updateTokens: (tokens: AuthTokens) => void;
  setRefreshing: (isRefreshing: boolean) => void;
  logout: (reason?: string) => void;
  initialize: () => Promise<void>;
  checkTokenExpiry: () => boolean;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      accessToken: null,
      refreshToken: null,
      status: AuthStatus.UNINITIALIZED,
      isAuthenticated: false,
      isRefreshing: false,
      authError: null,

      // Login action
      login: (tokens, user) => {
        set({
          user,
          accessToken: tokens.accessToken,
          refreshToken: tokens.refreshToken || null,
          status: AuthStatus.READY,
          isAuthenticated: true,
          authError: null,
        });

        // Emit login event for side effects (Firebase messaging, analytics, etc.)
        authEventEmitter.emit(AuthEvent.USER_LOGGED_IN);
      },

      // Update user data without emitting login event (e.g. refreshing profile)
      updateUser: (user) => {
        set({ user });
      },

      updateTokens: (tokens) => {
        set((state) =>
          state.isAuthenticated
            ? {
                accessToken: tokens.accessToken,
                refreshToken: tokens.refreshToken || state.refreshToken,
              }
            : state
        );
      },

      setRefreshing: (isRefreshing) => set({ isRefreshing }),

      // Logout action - clean auth state without navigation
      logout: (reason) => {
        // Emit logout event for side effects (Firebase cleanup, analytics, etc.)
        authEventEmitter.emit(AuthEvent.USER_LOGGED_OUT);

        // Clear auth state
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          status: AuthStatus.READY, // Keep ready so no loading screen shows
          isAuthenticated: false,
          isRefreshing: false,
          authError: null,
        });

        // Clear salon state
        useSalonStore.getState().clearSalons();

        // Note: Navigation is now handled by calling components
        if (reason) {
        }
      },

      // Clear error
      clearError: () => set({ authError: null }),

      // Initialize authentication
      initialize: async () => {
        const state = get();

        // Skip if already ready or currently initializing
        if (state.status === AuthStatus.READY || state.status === AuthStatus.INITIALIZING) {
          return;
        }

        set({ status: AuthStatus.INITIALIZING });

        try {
          // If no token, mark as ready but not authenticated
          if (!state.accessToken) {
            set({
              status: AuthStatus.READY,
              isAuthenticated: false,
            });
            return;
          }

          const { isValid } = authService.validateToken(state.accessToken);

          if (!isValid) {
            if (!state.refreshToken) {
              get().logout("Session expired. Please log in again.");
              set({ status: AuthStatus.READY });
              return;
            }

            try {
              const tokens = await authService.refreshToken(state.refreshToken);
              get().updateTokens(tokens);
              set({ status: AuthStatus.READY, isAuthenticated: true });
            } catch {
              get().logout("Session expired. Please log in again.");
              set({ status: AuthStatus.READY });
            }
            return;
          }

          set({
            status: AuthStatus.READY,
            isAuthenticated: true,
          });

          if (!state.refreshToken) {
            void authService
              .issueRefreshToken()
              .then((refreshToken) =>
                get().updateTokens({ accessToken: get().accessToken ?? "", refreshToken })
              )
              .catch(() => {});
          }
        } catch (error) {
          console.error("Auth initialization error:", error);

          // On error, logout to be safe
          get().logout("Authentication error");
          set({ status: AuthStatus.READY });
        }
      },

      // Check token expiry
      checkTokenExpiry: () => {
        const { accessToken, logout } = get();
        if (!accessToken) return false;

        const { isValid } = authService.validateToken(accessToken);
        if (!isValid) {
          logout("Session expired");
          return false;
        }

        return true;
      },
    }),
    {
      name: "auth-storage",
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
        // Note: status is not persisted, always starts as UNINITIALIZED
      }),
    }
  )
);
